import { ENABLE_SECURITY_DEBUG } from '../../constants';
import {
    EntityActionPermissionMappingError,
    FeatureActionPermissionMappingError,
    FeaturePermissionTokenValidationError
} from '../../errors';
import type {
    EntitySecurityContextEntry,
    FeatureName,
    SecurityAction,
    SecurityDecision,
    SecurityDecisionBatch,
    SecurityTarget
} from '../../types';
import {
    isFeaturePermissionAllowed,
    resolveEntityPermissionFromAction,
    resolveFeaturePermissionFromAction,
    resolveFeaturePermissionsForSecurity
} from '../../utils';

type SecurityDecisionSource = SecurityDecision['source'];
type SecurityDenyReason = Exclude<SecurityDecision['reason'], 'ALLOWED'>;

export type SecurityEvaluationContext = {
    userProfile: Parameters<typeof resolveFeaturePermissionsForSecurity>[0] | undefined;
    application: Parameters<typeof resolveFeaturePermissionsForSecurity>[1] | undefined;
    activeOrganizationAccount: Parameters<typeof resolveFeaturePermissionsForSecurity>[2] | undefined;
    featureNames: FeatureName[];
    entitySecurityContexts: EntitySecurityContextEntry[];
};

export function evaluateSecurityDecision(
    action: SecurityAction,
    target: SecurityTarget | undefined,
    {
        userProfile,
        application,
        activeOrganizationAccount,
        featureNames,
        entitySecurityContexts
    }: SecurityEvaluationContext
): SecurityDecision {
    const usedExplicitFeature = Boolean(target?.featureName);
    const usedExplicitEntity = Boolean(target?.entityName);
    const usedExplicitEntitySecurity = Boolean(target?.entitySecurity);

    let decisionSource: SecurityDecision['source'] = {
        action,
        featureName: target?.featureName,
        entityName: target?.entityName,
        usedExplicitFeature,
        usedExplicitEntity,
        usedExplicitEntitySecurity
    };

    try {
        const isPending = !userProfile || !application || !activeOrganizationAccount;

        if (isPending) {
            const decision = makeDenyDecision('DENY_PENDING', decisionSource, true);
            return decision;
        }

        const resolvedFeatureName = target?.featureName ?? featureNames[0];

        if (!resolvedFeatureName) {
            const decision = makeDenyDecision('DENY_FEATURE_SCOPE', decisionSource);
            return decision;
        }

        decisionSource = {
            ...decisionSource,
            featureName: resolvedFeatureName
        };

        const featurePermissions = resolveFeaturePermissionsForSecurity(
            userProfile,
            application,
            activeOrganizationAccount,
            resolvedFeatureName
        );

        const featurePermissionToCheck = resolveFeaturePermissionFromAction(action, target?.entityName);

        const isFeatureAllowed = featurePermissions
            ? isFeaturePermissionAllowed(featurePermissions, featurePermissionToCheck)
            : false;

        if (!isFeatureAllowed) {
            const decision = makeDenyDecision('DENY_FEATURE_PERMISSION', decisionSource);
            return decision;
        }

        if (!target?.entityName) {
            const decision = makeAllowDecision(decisionSource);
            return decision;
        }

        let resolvedEntitySecurity = target?.entitySecurity;

        if (!resolvedEntitySecurity) {
            /**
             * potential performance improvement:
             * if the entity security context is structured as a map instead of an array,
             * we can do a direct lookup here instead of iterating through an array to find the matching
             * entity security context. We'd need to ensure the sequencing semantic is preserved in the context
             * value if we go this route, as that sequencing is important for the security resolution process
             */
            const matchedEntitySecurityContext = entitySecurityContexts.find(
                (entry) => entry.entityName === target?.entityName
            );
            resolvedEntitySecurity = matchedEntitySecurityContext?.entitySecurity;
        }

        if (!resolvedEntitySecurity) {
            const decision = makeDenyDecision('DENY_ENTITY_SCOPE', decisionSource);
            return decision;
        }

        if (!Array.isArray(resolvedEntitySecurity.allowedActions)) {
            const decision = makeDenyDecision('DENY_ROW_SECURITY_MISSING', decisionSource);
            return decision;
        }

        const entityPermissionToCheck = resolveEntityPermissionFromAction(action);

        const isEntityAllowed = resolvedEntitySecurity.allowedActions.includes(entityPermissionToCheck);

        if (!isEntityAllowed) {
            const decision = makeDenyDecision('DENY_ROW_SECURITY_ACTION', decisionSource);
            return decision;
        }

        const decision = makeAllowDecision(decisionSource);

        return decision;
    } catch (error) {
        console.error('Security decision evaluation failed', {
            source: decisionSource,
            error
        });

        if (error instanceof FeatureActionPermissionMappingError) {
            const decision = makeDenyDecision('DENY_UNKNOWN_ACTION', decisionSource);

            return decision;
        }

        if (
            error instanceof EntityActionPermissionMappingError ||
            error instanceof FeaturePermissionTokenValidationError
        ) {
            const decision = makeDenyDecision('DENY_UNKNOWN_MAPPING', decisionSource);

            return decision;
        }

        const decision = makeDenyDecision('DENY_RUNTIME_ERROR', decisionSource);

        return decision;
    }
}

/**
 * Evaluates one or more security actions against the same target and context.
 *
 * Shared pure-logic entry point used by `useSecurity` and `useEntitySecurityEvaluator`
 * so single vs batch orchestration is not duplicated in the hooks.
 */
export function evaluateSecurityDecisions(
    actionOrActions: SecurityAction | SecurityAction[],
    target: SecurityTarget | undefined,
    evaluationContext: SecurityEvaluationContext
): SecurityDecision | SecurityDecisionBatch {
    if (Array.isArray(actionOrActions)) {
        const decisions = actionOrActions.map((action) =>
            evaluateSecurityDecision(action, target, evaluationContext)
        );
        const decision: SecurityDecisionBatch = {
            allowed: decisions.map((entry) => entry.allowed),
            decisions
        };

        return decision;
    }

    const decision = evaluateSecurityDecision(actionOrActions, target, evaluationContext);

    return decision;
}

function makeAllowDecision(source: SecurityDecisionSource): SecurityDecision {
    const decision: SecurityDecision = {
        allowed: true,
        pending: false,
        reason: 'ALLOWED',
        source
    };

    return decision;
}

function makeDenyDecision(
    reason: SecurityDenyReason,
    source: SecurityDecisionSource,
    pending = false
): SecurityDecision {
    const decision: SecurityDecision = {
        allowed: false,
        pending,
        reason,
        source
    };

    if (ENABLE_SECURITY_DEBUG) {
        /**
         * This conditional block will get removed from the production bundle.
         *
         * During development, this provides useful debugging information about security evaluations.
         *
         * Using the `console.warn()` API causes the logged message to include the stack trace, thus revealing the
         * component chain where the denied security decision was evaluated. This provides better context
         * about why certain UI elements are not rendering or certain actions are not available,
         * and helps identify any potential misconfigurations in the feature/entity security providers or
         * missing `security` fields in the API responses.
         */
        console.warn('Security decision denied for', {
            feature: decision.source.featureName,
            entity: decision.source.entityName,
            action: decision.source.action,
            reason: decision.reason
        });
    }

    return decision;
}
